import React, { useState } from "react";
import { useUser } from "../../contexts/UserContext";

const EmailOtpSignIn = ({ onSuccess, compact = false }) => {
  const { requestEmailOtp, verifyEmailOtp } = useUser();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [requestId, setRequestId] = useState("");
  const [phase, setPhase] = useState("email");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const normalizedEmail = email.trim().toLowerCase();
  const sanitizedCode = code.replace(/\D/g, "").slice(0, 6);
  const canSend = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail);
  const canVerify = requestId && /^\d{6}$/.test(sanitizedCode);

  const sendCode = async (event) => {
    event.preventDefault();
    if (!canSend || loading) return;
    setLoading(true);
    setError("");
    setMessage("");
    try {
      const result = await requestEmailOtp(normalizedEmail);
      setRequestId(result.request_id);
      setPhase("code");
      setMessage("We sent a 6-digit code to your email.");
    } catch (err) {
      setError(err.message || "Could not send the sign-in code.");
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (event) => {
    event.preventDefault();
    if (!canVerify || loading) return;
    setLoading(true);
    setError("");
    try {
      const result = await verifyEmailOtp(normalizedEmail, requestId, sanitizedCode);
      if (result?.success) {
        onSuccess?.(result);
      } else {
        setError(result?.error || "Could not verify the sign-in code.");
      }
    } catch (err) {
      setError(err.message || "Could not verify the sign-in code.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setPhase("email");
    setCode("");
    setRequestId("");
    setMessage("");
    setError("");
  };

  const inputClass =
    "w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm text-gray-800 outline-none transition-colors placeholder:text-gray-400 focus:border-[#402c7f] focus:ring-2 focus:ring-[#402c7f]/10";
  const buttonClass =
    "w-full rounded-xl px-4 py-3 text-sm font-semibold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50";

  return (
    <div className={compact ? "space-y-3" : "space-y-3 text-left"}>
      {phase === "email" ? (
        <form onSubmit={sendCode} className="space-y-3">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email address"
            autoComplete="email"
            className={inputClass}
          />
          <button
            type="submit"
            disabled={!canSend || loading}
            className={buttonClass}
            style={{ background: "#402c7f" }}
          >
            {loading ? "Sending code..." : "Continue with email"}
          </button>
        </form>
      ) : (
        <form onSubmit={verifyCode} className="space-y-3">
          <div className="rounded-xl border border-[#d9d1ef] bg-[#f8f6ff] px-4 py-3">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-gray-800">{normalizedEmail}</p>
                <p className="mt-0.5 text-xs text-gray-500">Enter the code we sent to this address.</p>
              </div>
              <button
                type="button"
                onClick={reset}
                className="shrink-0 text-xs font-semibold text-[#402c7f] hover:text-[#ee2a7b]"
              >
                Change
              </button>
            </div>
          </div>
          <div className="relative">
            <input
              type="tel"
              value={sanitizedCode}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="000000"
              inputMode="numeric"
              autoComplete="one-time-code"
              aria-label="6-digit sign-in code"
              className={`${inputClass} h-14 text-center font-mono text-lg font-semibold tracking-[0.32em] placeholder:tracking-[0.32em]`}
            />
          </div>
          <button
            type="submit"
            disabled={!canVerify || loading}
            className={buttonClass}
            style={{ background: "#402c7f" }}
          >
            {loading ? "Verifying..." : "Sign in with code"}
          </button>
          <button
            type="button"
            onClick={sendCode}
            disabled={loading}
            className="w-full text-xs font-semibold text-gray-500 hover:text-[#402c7f] disabled:opacity-50"
          >
            Send a new code
          </button>
        </form>
      )}

      {(message || error) && (
        <p className={`text-center text-xs ${error ? "text-red-600" : "text-gray-500"}`}>
          {error || message}
        </p>
      )}
    </div>
  );
};

export default EmailOtpSignIn;